import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import { slugifyForUrl } from '../lib/areaSlug';
import {
  getListingAreaGroupKey,
  getListingAreaGroupLabel,
  listingMatchesBrowseCity,
} from '../lib/locationRegions';
import { usePublishedListings } from '../hooks/usePublishedListings';

export default function Area() {
  const params = useParams<{ citySlug?: string }>();
  const citySlug = (params.citySlug || '').trim().toLowerCase();

  const { data: listings, isLoading } = usePublishedListings();

  const cities = useMemo(() => {
    const src = listings || [];
    const byCity = new Map<string, { label: string; count: number }>();
    for (const l of src) {
      const label = getListingAreaGroupLabel(l);
      const key = getListingAreaGroupKey(l);
      if (!key) continue;
      const prev = byCity.get(key);
      byCity.set(key, { label, count: (prev?.count || 0) + 1 });
    }
    return [...byCity.values()].sort((a, b) => b.count - a.count);
  }, [listings]);

  const activeCityLabel = useMemo(() => {
    if (!citySlug) return '';
    const match = cities.find((c) => slugifyForUrl(c.label) === citySlug);
    return match?.label || '';
  }, [cities, citySlug]);

  const filtered = useMemo(() => {
    if (!listings || !activeCityLabel) return [];
    return listings.filter((l) => listingMatchesBrowseCity(l, activeCityLabel));
  }, [listings, activeCityLabel]);

  if (!citySlug) return <Navigate to="/" replace />;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]" />
        </div>
      </div>
    );
  }

  if (!activeCityLabel) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold mb-3">Area not found</h1>
        <p className="text-gray-600 mb-6">
          This area doesn’t currently have any published listings.
        </p>
        <Link to="/" className="text-[#32373c] font-medium hover:underline">
          Back to home →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{activeCityLabel}</h1>
        <p className="text-gray-600">
          Browse published properties in {activeCityLabel}.
        </p>
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
        </div>
      ) : (
        <p className="text-gray-600">No published listings in this area yet.</p>
      )}
    </div>
  );
}
